import { useEffect, useState, useCallback } from "react";
import ReceiptDetailUI from "./ReceiptDetailUI";
import { IProductSub, IReceiptDetail } from "src/types/global";
import { useLocation } from "react-router-dom";
import { authService } from "@services/authService";
import { receiptService } from "@services/ReceiptService";
import { createHeader, formatDate, getCurrency } from "@utils/utils";
import { Stack, Tooltip, Typography } from "@mui/material";
import MyButton from "@components/atoms/MyButton/MyButton";
import { Trash } from "react-bootstrap-icons";
const ReceiptDetail = () => {
  const location = useLocation();
  const [receipt, setReceipt] = useState<IReceiptDetail>({
    id: "- - -",
    name: "- - -",
    clientId: -1,
    clientName: "- - -",
    clientIdentification: "- - -",
    clientPhone: "- - -",
    limit: "- - -",
    lastTransactionTime: null,
    creationTime: "- - -",
    requestProducts: [],
    returnProducts: [],
    total: 0,
    type: 0,
    status: false,
    createdByEmployeeName: "",
    lastModifiedByEmployeeName: ""
  });
  const [rows, setRows] = useState<IProductSub[]>([]);
  const [rows2, setRows2] = useState<IProductSub[]>([]);
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPage2, setCurrentPage2] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Hubo problemas para consultar");
  const [showDialogDelete, setShowDialogDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [employeeCode, setEmployeeCode] = useState("");
  const [applicant, setApplicant] = useState("");
  const [product, setProduct] = useState<IProductSub | undefined>(undefined);
  const rowsPerPage = 10;
  const getReceipt = useCallback(async (id: number) => {
    try {
      setIsLoading(true);
      const res = await receiptService.get(id);
      const receiptDB = res.data;
      const lastTransactionTime = receiptDB.lastTransactionTime;
      setPages(Math.ceil(receiptDB.requestProducts.length / rowsPerPage));
      setReceipt(oldReceipt => ({
        ...oldReceipt,
        ...receiptDB,
        creationTime: formatDate(receiptDB.creationTime),
        limit: formatDate(receiptDB.limit),
        lastTransactionTime: lastTransactionTime ? formatDate(lastTransactionTime) : lastTransactionTime,
        requestProducts: [...receiptDB.requestProducts.map(prod => ({
          ...prod,
          productRender: <Tooltip placement="top" title={prod.productReference} sx={{
            cursor: "pointer"
          }}>
                <Typography>{prod.productName}</Typography>
              </Tooltip>,
          employeeName: prod.createdByEmployeeName ? <Tooltip placement="top" title={`Solicitado por: ${prod.applicant}`} sx={{
            cursor: "pointer"
          }}>
                  <Typography>{prod.createdByEmployeeName}</Typography>
                </Tooltip> : "N/A",
          price: getCurrency(prod.price),
          requestedTime: formatDate(prod.requestedTime),
          totalCurrency: getCurrency(prod.total)
        }))],
        returnProducts: [...receiptDB.returnProducts.map(prod => ({
          ...prod,
          productRender: <Tooltip placement="top" title={prod.productReference} sx={{
            cursor: "pointer"
          }}>
                <Typography>{prod.productName}</Typography>
              </Tooltip>,
          employeeName: prod.createdByEmployeeName ? <Tooltip placement="top" title={`Solicitado por: ${prod.applicant}`} sx={{
            cursor: "pointer"
          }}>
                  <Typography>{prod.createdByEmployeeName}</Typography>
                </Tooltip> : "N/A",
          price: getCurrency(prod.price),
          requestedTime: formatDate(prod.requestedTime),
          totalCurrency: getCurrency(-prod.total)
        }))]
      }));
    } catch (error) {
      setErrorMessage("Hubo problemas para consultar");
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    const id = location.pathname.split("/").reverse()[0];
    const routeState: IReceiptDetail = location.state;
    if (id === routeState?.id) {
      setReceipt(oldReceipt => ({
        ...oldReceipt,
        id: id,
        clientName: routeState.clientName,
        total: routeState.total
      }));
    }
    getReceipt(Number(id));
  }, [getReceipt, location]);
  useEffect(() => {
    setRows([...receipt.requestProducts.slice(rowsPerPage * (currentPage - 1), rowsPerPage * currentPage).map(reqProduct => ({
      ...reqProduct,
      actions: <Stack direction={"row"} justifyContent={"center"} spacing={1}>
              <Tooltip title="Eliminar" placement="right">
                <MyButton color="error" onClick={() => {
            setProduct(reqProduct);
            setShowDialogDelete(true);
          }} disabled={isDeleting}>
                  <Trash size={22} />
                </MyButton>
              </Tooltip>
            </Stack>
    }))]);
  }, [receipt, currentPage, isDeleting]);
  useEffect(() => {
    setRows2([...receipt.returnProducts.slice(rowsPerPage * (currentPage2 - 1), rowsPerPage * currentPage2).map(retProduct => ({
      ...retProduct,
      actions: <Stack direction={"row"} justifyContent={"center"} spacing={1}>
              <Tooltip title="Eliminar" placement="right">
                <MyButton color="error" onClick={() => {
            setProduct(retProduct);
            setShowDialogDelete(true);
          }} disabled={isDeleting}>
                  <Trash size={22} />
                </MyButton>
              </Tooltip>
            </Stack>
    }))]);
  }, [receipt, currentPage2, isDeleting]);
  const handleDelete = useCallback(async () => {
    try {
      setIsDeleting(true);
      const receiptDetailId = product?.receiptDetailId;
      if (!receiptDetailId || !employeeCode) {
        throw new Error();
      }
      let receiptIdNumber = Number(receipt.id);
      const payload = {
        receiptId: receiptIdNumber,
        receiptDetailId,
        operation: 2,
        employeeCode,
        applicant
      };
      await receiptService.put(payload);
      await getReceipt(receiptIdNumber);
      setEmployeeCode("");
      setApplicant("");
    } catch (error) {
      setErrorMessage(`Lo sentimos, no se pudo eliminar ${product?.productName}`);
      setShowError(true);
    } finally {
      setIsDeleting(false);
      setShowDialogDelete(false);
    }
  }, [product, employeeCode, applicant, receipt.id, getReceipt]);
  const headers = [createHeader("Fecha", "requestedTime"), createHeader("Producto", "productRender"), createHeader("Cantidad", "quantity"), createHeader("Precio unidad", "price"), createHeader("Total", "totalCurrency"), createHeader("Autor", "employeeName"), createHeader("Acciones", "actions")].filter(item => item.id === "actions" && authService.isAdmin() || item.id !== "actions");
  return <ReceiptDetailUI id={receipt.id} name={receipt.name} type={receipt.type} clientId={receipt.clientId} clientName={receipt.clientName} creationTime={receipt.creationTime} limit={receipt.limit} createdByEmployeeName={receipt.createdByEmployeeName} lastModifiedByEmployeeName={receipt.lastModifiedByEmployeeName} total={receipt.total} total2={getCurrency(-receipt.returnProducts.reduce((total, prod) => total + prod.total, 0))} headers={headers} rows={rows} rows2={rows2} pages={pages} onChangePage={setCurrentPage} onChangePage2={setCurrentPage2} isLoading={isLoading} errorMessage={errorMessage} showError={showError} closeError={() => setShowError(false)} receipt={receipt} setReceipt={setReceipt} showDialog={showDialogDelete} handleConfirmDialog={handleDelete} handleCloseDialog={() => {
    setShowDialogDelete(false);
    setEmployeeCode("");
    setApplicant("");
  }} employeeCode={employeeCode} setEmployeeCode={setEmployeeCode} applicant={applicant} setApplicant={setApplicant} />;
};
export default ReceiptDetail;